import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import CrossIcon from "assets/icons/cross.svg?react";
import ChevronLeftIcon from "assets/icons/chevron-left.svg?react";

import {
  InvestObjectsProps,
  InvestObjectsFormProps,
  InvestObjectsFieldsProps,
} from "./InvestObjects.types";

import {
  CALCULATOR_TYPES,
  getGrowthDynamicOptions,
  CALCULATOR_OBJECT_TYPES,
} from "constants/calculator";

import { checkFieldConfiguration } from "helpers/checkFieldConfiguration";

import { getRentPerSqMeter } from "./InvestObjects.helpers";

import {
  visibleFields,
  disabledFields,
  defaultFieldProps,
  defaultFieldGroupProps,
} from "./InvestObjects.config";

import { getParkingTypeName } from "../InvestObjectsHeader/InvestObjectsHeader.helpers";

import { Typography } from "shared/ui/components/Typography";

import { CalculatorFormikField } from "components/CalculatorFormikField";
import { ButtonGroupField, InputField, SelectField } from "components/FormikFields";
import { FormikField } from "components/FormikField";

import { FieldGroup } from "../FieldGroup";
import { AddObjectButton } from "../AddObjectButton";
import { InvestObjectsHeader } from "../InvestObjectsHeader";

import styles from "./InvestObjects.module.scss";
import { InvestObjectsTabs } from "../InvestObjectsTabs";

const ApartmentFields = ({
  isMobile = false,
  t,
  index,
  isRootAdmin,
  calculatorType,
}: InvestObjectsFieldsProps) => {
  const { values } = useFormikContext<any>();

  return (
    <div className={styles.fields}>
      <div
        className={cn(styles.fieldsColumn, {
          [styles.fieldsColumnStandard]: calculatorType === CALCULATOR_TYPES.BASIC,
        })}
      >
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Allgemeine Parameter")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`apartments[${index}].aptArea`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptArea
          )}
          component={InputField}
          controlProps={{
            label: t("calculator_form.invest_objects_fields.apartment.area"),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.aptArea
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">m²</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />
        {isRootAdmin && (
          <CalculatorFormikField
            name={`apartments[${index}].aptDinSize`}
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.aptDinSize
            )}
            component={InputField}
            controlProps={{
              label: t(
                "calculator_form.invest_objects_fields.apartment.area_DIN277"
              ),
              ...defaultFieldProps,
            }}
            componentProps={{
              type: "localeDe",
              disabled: !checkFieldConfiguration(
                calculatorType,
                disabledFields.aptDinSize
              ),
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">m²</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
        )}

        {calculatorType === CALCULATOR_TYPES.BASIC && (
          <CalculatorFormikField
            name={`apartments[${index}].aptPrice`}
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.aptPrice
            )}
            component={InputField}
            controlProps={{
              label: t("calculator_form.invest_objects_fields.apartment.price"),
              ...defaultFieldProps,
            }}
            componentProps={{
              type: "localeDe",
              disabled: !checkFieldConfiguration(
                calculatorType,
                disabledFields.aptPrice
              ),
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
        )}

        {calculatorType === CALCULATOR_TYPES.BASIC && (
          <CalculatorFormikField
            name={`apartments[${index}].aptRentPerMonth`}
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.aptRentPerMonth
            )}
            component={InputField}
            controlProps={{
              label: t(
                "calculator_form.invest_objects_fields.apartment.rent_per_month"
              ),
              ...defaultFieldProps,
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: (
                <div className={styles.rentEndAdornment}>
                  <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>
                  <div className={styles.rentEndAdornmentDivider} />
                  <Typography className={styles.currency} variant="bodyM" weight="medium">
                    {getRentPerSqMeter({
                      aptRentPerMonth:
                        values.apartments[index]?.aptRentPerMonth,
                      aptArea: values.apartments[index]?.aptArea,
                    })}
                  </Typography>
                </div>
              ),
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
        )}

        <CalculatorFormikField
          name={`apartments[${index}].aptLandShare`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptLandShare
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.apartment.land_share"
            ),
            variant: "horizontal",
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.aptLandShare
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <CalculatorFormikField
          name={`apartments[${index}].aptDiscount`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptDiscount
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.apartment.price_discount"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.aptDiscount
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <CalculatorFormikField
          name={`apartments[${index}].aptFurniturePrice`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptFurniturePrice
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.apartment.furniture_price"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.aptFurniturePrice
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />
      </div>

      <div
        className={cn(styles.fieldsColumn, {
          [styles.fieldsColumnStandardHidden]:
            calculatorType === CALCULATOR_TYPES.BASIC,
        })}
      >
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("calculator_form.invest_objects_fields.apartment.price")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`apartments[${index}].aptPrice`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptPrice
          )}
          component={InputField}
          controlProps={{
            label: t("calculator_form.invest_objects_fields.apartment.price"),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.aptPrice
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <FieldGroup
          label={t(
            "calculator_form.invest_objects_fields.apartment.price_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptPriceGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            {
              [styles.fieldGroupLabelSingle]: isMobile
            }
          )}
        >
          <FormikField
            name={`apartments[${index}].aptPriceGrowth`}
            component={InputField}
            withoutError={true}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle }),
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper,
            }}
          />
          {!isMobile && <FormikField
            name={`apartments[${index}].aptPriceGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              placeholder: t("calculator_form."),
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            name={`apartments[${index}].aptPriceGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }

        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Nettokaltmiete")}
          </Typography>
        </div>

        <CalculatorFormikField
          name={`apartments[${index}].aptRentPerMonth`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptRentPerMonth
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.apartment.rent_per_month"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: (
              <div className={styles.rentEndAdornment}>
                <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>
                <div className={styles.rentEndAdornmentDivider} />
                <Typography
                  variant="bodyS"
                  className={styles.rentEndAdornmentMonthly}
                >
                  {getRentPerSqMeter({
                    aptRentPerMonth:
                      values.apartments[index]?.aptRentPerMonth,
                    aptArea: values.apartments[index]?.aptArea,
                  })}
                </Typography>
              </div>
            ),
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <FieldGroup
          label={t(
            "calculator_form.invest_objects_fields.apartment.rent_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.aptRentGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            {
              [styles.fieldGroupLabelSingle]: isMobile
            }
          )}
        >
          <FormikField
            name={`apartments[${index}].aptRentGrowth`}
            component={InputField}
            withoutError={true}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle }),
            }}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper,
            }}
          />
          {!isMobile && <FormikField
            name={`apartments[${index}].aptRentGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            name={`apartments[${index}].aptRentGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }
      </div>
    </div>
  );
};

const ParkingFields = ({
  isMobile = false,
  t,
  index,
  isRootAdmin,
  calculatorType,
}: InvestObjectsFieldsProps) => {
  return (
    <div className={styles.fields}>
      <div className={styles.fieldsColumn}>

        {isRootAdmin && (
          <CalculatorFormikField
            name={`parkings[${index}].parkingDinSize`}
            hidden={checkFieldConfiguration(
              calculatorType,
              visibleFields.parkingDinSize
            )}
            component={InputField}
            controlProps={{
              label: t(
                "calculator_form.invest_objects_fields.parking_space.area_DIN277"
              ),
              ...defaultFieldProps,
            }}
            componentProps={{
              type: "localeDe",
              disabled: !checkFieldConfiguration(
                calculatorType,
                disabledFields.parkingDinSize
              ),
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper
            }}
          />
        )}
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Kaufpreis")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`parkings[${index}].parkingPrice`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingPrice
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.parking_space.price"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.parkingPrice
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />
        <CalculatorFormikField
          name={`parkings[${index}].parkingDiscount`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingDiscount
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.parking_space.discount"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.parkingDiscount
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />
        <FieldGroup
          label={t(
            "calculator_form.invest_objects_fields.parking_space.price_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingPriceGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            { [styles.fieldGroupLabelSingle]: isMobile }
          )}
        >
          <FormikField
            name={`parkings[${index}].parkingPriceGrowth`}
            component={InputField}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle }),
            }}
            withoutError={true}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper,
            }}
          />
          {!isMobile && <FormikField
            name={`parkings[${index}].parkingPriceGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            name={`parkings[${index}].parkingPriceGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            className={styles.growthDynamicButtons}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }
      </div>


      <div className={styles.fieldsColumn}>
        <div className={cn(styles.header, { [styles.proHeader]: calculatorType === CALCULATOR_TYPES.STANDARD })}>
          <Typography
            className={styles.headerName}
            variant="bodyS"
            component="span"
          >
            {t("Parkplatzmiete ")}
          </Typography>
        </div>
        <CalculatorFormikField
          name={`parkings[${index}].parkingLandShare`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingLandShare
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.parking_space.land_share"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            disabled: !checkFieldConfiguration(
              calculatorType,
              disabledFields.parkingLandShare
            ),
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <CalculatorFormikField
          name={`parkings[${index}].parkingRentPerMonth`}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingRentPerMonth
          )}
          component={InputField}
          controlProps={{
            label: t(
              "calculator_form.invest_objects_fields.parking_space.rent_per_month"
            ),
            ...defaultFieldProps,
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />

        <FieldGroup
          label={t(
            "calculator_form.invest_objects_fields.parking_space.rent_growth_rate"
          )}
          hidden={checkFieldConfiguration(
            calculatorType,
            visibleFields.parkingRentGrowth
          )}
          className={styles.fieldGroup}
          labelClassName={cn(styles.fieldGroupLabel,
            { [styles.fieldGroupLabelSingle]: isMobile }
          )}
        >
          <FormikField
            name={`parkings[${index}].parkingRentGrowth`}
            component={InputField}
            controlProps={{
              ...defaultFieldGroupProps,
              ...(isMobile && { className: styles.fieldGroupControlSingle }),
            }}
            withoutError={true}
            componentProps={{
              type: "localeDe",
              placeholder: "0",
              endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">%</Typography>,
              withoutBorder: true,
              fieldClassName: styles.input,
              className: styles.inputWrapper,
            }}
          />
          {!isMobile && <FormikField
            name={`parkings[${index}].parkingRentGrowthDynamic`}
            component={SelectField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              size: "small",
              options: getGrowthDynamicOptions(t),
              withoutBorder: true,
              controlClassName: styles.selectField,
              menuClassName: styles.selectFieldMenu
            }}
          />}
        </FieldGroup>
        {isMobile &&
          <FormikField
            name={`parkings[${index}].parkingRentGrowthDynamic`}
            component={ButtonGroupField}
            controlProps={defaultFieldGroupProps}
            componentProps={{
              type: "field",
              list: getGrowthDynamicOptions(t),
              className: styles.growthDynamicButton,
              activeClassName: styles.growthDynamicButtonActive,
              size: "small"
            }}
          />
        }
      </div>
    </div>
  );
};

const InvestObjectsDesktop = ({
  t,
  values,
  isRootAdmin,
  activeObject,
  calculatorType,
  onAddObjectClick,
  onObjectRemoveClick,
  onObjectClick,
}: InvestObjectsFormProps) => {
  return (
    <div className={styles.rootDesktop}>
      <InvestObjectsHeader
        values={values}
        calculatorType={calculatorType}
        activeObject={activeObject}
        onAddObjectClick={onAddObjectClick}
        onObjectRemoveClick={onObjectRemoveClick}
        onObjectClick={onObjectClick}
      />
      {activeObject.type === CALCULATOR_OBJECT_TYPES.APARTMENT && (
        <ApartmentFields
          t={t}
          index={activeObject.index}
          isRootAdmin={isRootAdmin}
          calculatorType={calculatorType}
        />
      )}
      {activeObject.type === CALCULATOR_OBJECT_TYPES.PARKING && (
        <ParkingFields
          t={t}
          index={activeObject.index}
          isRootAdmin={isRootAdmin}
          calculatorType={calculatorType}
        />
      )}
    </div>
  );
};

const InvestObjectsMobile = ({
  t,
  values,
  isRootAdmin,
  calculatorType,
  activeObject,
  onAddObjectClick,
  onObjectRemoveClick,
  onObjectClick,
}: InvestObjectsFormProps) => {

  return (
    <div className={styles.rootMobile} >
      {activeObject.type === null &&
        <InvestObjectsTabs
          values={values}
          calculatorType={calculatorType}
          activeObject={activeObject}
          onObjectClick={onObjectClick}
        />
      }
      <div className={cn(styles.header, { [styles.standardHeader]: calculatorType === CALCULATOR_TYPES.BASIC })}>
        <Typography
          className={styles.headerName}
          variant="bodyS"
          component="span"
        >
          {t("calculator_form.invest_objects.title")}
        </Typography>
      </div>

      {
        activeObject && activeObject.type === CALCULATOR_OBJECT_TYPES.PARKING && (
          <div key={`parking-active`} className={styles.mobileItem}>
            <div className={styles.mobileHeader}>
              <ChevronLeftIcon
                className={styles.mobileRemoveIcon}
                name="chevronLeft"
                onClick={() => onObjectClick(null, null)}
              />
              <Typography variant="h4" weight="semiBold" className={styles.mobileTitle}>
                {getParkingTypeName(activeObject.parkingType, t)}
              </Typography>
              {calculatorType === CALCULATOR_TYPES.STANDARD &&
                <CrossIcon
                  className={styles.mobileRemoveIcon}
                  name="cross"
                  onClick={() => {
                    onObjectRemoveClick(CALCULATOR_OBJECT_TYPES.PARKING, activeObject?.index);
                    onObjectClick(null, null);
                  }}
                />
              }
            </div>

            <ParkingFields
              t={t}
              index={activeObject?.index}
              isRootAdmin={isRootAdmin}
              calculatorType={calculatorType}
              isMobile={true}
            />
          </div>
        )
      }
      {
        activeObject && activeObject.type === CALCULATOR_OBJECT_TYPES.APARTMENT && (
          <div key={`apartment-${activeObject.index}`} className={styles.mobileItem}>
            {calculatorType !== CALCULATOR_TYPES.BASIC && <div className={styles.mobileHeader}>
              <ChevronLeftIcon
                className={styles.mobileRemoveIcon}
                name="chevronLeft"
                onClick={() => onObjectClick(null, null)}
              />
              <Typography variant="h4" weight="semiBold" className={styles.mobileTitle}>
                {t("calculator_form.invest_objects.apartment")}
              </Typography>
              {activeObject.index !== 0 && (
                <CrossIcon
                  className={styles.mobileRemoveIcon}
                  name="cross"
                  onClick={() => {
                    onObjectRemoveClick(CALCULATOR_OBJECT_TYPES.APARTMENT, activeObject.index);
                    onObjectClick(null, null);
                  }}
                />
              )}

            </div>}
            <ApartmentFields
              t={t}
              index={activeObject.index}
              isRootAdmin={isRootAdmin}
              calculatorType={calculatorType}
              isMobile={true}
            />
          </div>
        )
      }

      {
        !activeObject.type && calculatorType === CALCULATOR_TYPES.STANDARD && (
          <AddObjectButton
            isMobile={true}
            onAddObjectClick={onAddObjectClick}
          />
        )
      }
    </div >
  );
};

const InvestObjects = ({
  activeTab,
  isDesktop,
  isRootAdmin,
  calculatorType,
  values,
  activeObject,
  onAddObjectClick,
  onObjectRemoveClick,
  onObjectClick,
}: InvestObjectsProps) => {
  const { t } = useTranslation();

  if (
    calculatorType !== CALCULATOR_TYPES.BASIC &&
    activeTab !== "investObjects"
  ) {
    return null;
  }

  if (!isDesktop) {
    return (
      <InvestObjectsMobile
        t={t}
        values={values}
        isRootAdmin={isRootAdmin}
        activeObject={activeObject}
        calculatorType={calculatorType}
        onAddObjectClick={onAddObjectClick}
        onObjectRemoveClick={onObjectRemoveClick}
        onObjectClick={onObjectClick}
      />
    );
  }

  return (
    <InvestObjectsDesktop
      t={t}
      values={values}
      isRootAdmin={isRootAdmin}
      activeObject={activeObject}
      calculatorType={calculatorType}
      onAddObjectClick={onAddObjectClick}
      onObjectRemoveClick={onObjectRemoveClick}
      onObjectClick={onObjectClick}
    />
  );
};

export default InvestObjects;
